import React from 'react';
import type { WithContext, Thing } from 'schema-dts';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
  jsonLd?: WithContext<Thing> | WithContext<Thing>[];
  noindex?: boolean;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  children?: React.ReactNode;
}

interface CourseSchemaProps {
  name: string;
  description: string;
  provider: string;
  url: string;
  image: string;
  price: number;
  duration: string;
}

interface ArticleSchemaProps {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  publisher: string;
}

export function generateCourseSchema(props: CourseSchemaProps): WithContext<any> {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": props.name,
    "description": props.description,
    "provider": {
      "@type": "Organization",
      "name": props.provider,
      "sameAs": "https://one80labs.com"
    },
    "url": props.url,
    "image": props.image,
    "offers": {
      "@type": "Offer",
      "price": props.price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "validFrom": new Date().toISOString()
    },
    "timeRequired": props.duration,
    "educationalLevel": "Beginner to Intermediate",
    "teaches": [
      "AI implementation",
      "Workflow automation",
      "Business productivity",
      "Technology optimization"
    ]
  };
}

export function generateArticleSchema(props: ArticleSchemaProps): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": props.headline,
    "description": props.description,
    "image": props.image,
    "datePublished": props.datePublished,
    "dateModified": props.dateModified,
    "author": {
      "@type": "Person",
      "name": props.author
    },
    "publisher": {
      "@type": "Organization",
      "name": props.publisher,
      "logo": {
        "@type": "ImageObject",
        "url": "https://one80labs.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://one80labs.com"
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["article", ".blog-title", ".blog-meta"]
    },
    "isAccessibleForFree": "True"
  };
}

export default function SEO({
  title = 'One80Labs - AI Training & Fractional Project Management in Omaha, Nebraska',
  description = 'Transform your business with AI-powered project management and training in Omaha and the Midwest. Expert services in ChatGPT, Gemini, and Microsoft 365 Copilot.',
  image = 'https://images.unsplash.com/photo-1677442135968-6d89485dd1d1',
  article = false,
  jsonLd,
  noindex = false,
  keywords = [],
  author,
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  children
}: SEOProps) {
  // This is a headless component that doesn't render anything visible
  // It's meant to be used with a head management library like react-helmet or react-helmet-async
  
  // Return the children to allow nesting
  return <>{children}</>;
}